import React, { useState, useEffect } from "react"
import { useShallow } from "zustand/react/shallow"

// components
import { PrimeButton } from "components-design-system"
import { InputNumber } from "components-design-system"
import CountryRow from "./CountryRow/CountryRow"
import TableVersionComparisonWrapper from "../../components/TableVersionComparisonWrapper/TableVersionComparisonWrapper"
import CellValidation from "../components/CellValidation/CellValidation"

// hooks
import useAuthoringDataStore from "../../../hooks/stores/useAuthoringDataStore"
import useAuthoringViewStore from "containers/studies/Study/subcomponents/Authoring/hooks/stores/useAuthoringViewStore"
import useAuthoringVersionStore from "../../../hooks/stores/useAuthoringVersionStore"

import "./RJTrialCountries.scss"

const RJTrialCountries = (props) => {
  const [trialCountriesFormData, setTrialCountriesFormData] = useState([])
  const [totalWorldwide, setTotalWorldwide] = useState(0)
  const [totalEEA, setTotalEEA] = useState(0)
  const [compareTotalWorldwide, setCompareTotalWorldwide] = useState(0)
  const [compareTotalEEA, setCompareTotalEEA] = useState(0)

  const { versionData, selectedVersion } = useAuthoringVersionStore(
    useShallow((state) => ({
      versionData: state.versionData,
      selectedVersion: state.selectedVersion,
    }))
  )

  const { formData } = useAuthoringDataStore(
    useShallow((state) => ({
      formData: state.formData,
    }))
  )

  const { readOnly, showVersionCompare, customTableWidthClassName } = useAuthoringViewStore(
    useShallow((state) => ({
      readOnly: state.readOnly,
      showVersionCompare: state.showVersionCompare,
      customTableWidthClassName: state.customTableWidthClassName,
    }))
  )

  let { id } = props

  let schema = {
    headers: [
      {
        key: "country_name",
        title: "Trial Country",
      },
      {
        key: "subjects",
        title: "Actual number of subjects enrolled",
      },
    ],
    footers: [
      {
        key: "worldwide",
        title: "Total: worldwide",
      },
      {
        key: "eea-only",
        title: "Total: EEA only",
      },
    ],
  }

  let eeaSet = new Set([
    "Belgium",
    "Bulgaria",
    "Czechia",
    "Czech Republic",
    "Denmark",
    "Cyprus",
    "Latvia",
    "Lithuania",
    "Luxembourg",
    "Spain",
    "France",
    "Croatia",
    "Italy",
    "Poland",
    "Portugal",
    "Romania",
    "Slovenia",
    "Hungary",
    "Malta",
    "Netherlands",
    "Austria",
    "Iceland",
    "Liechtenstein",
    "Norway",
    "Slovakia",
    "Finland",
    "Sweden",
    "Germany",
    "Estonia",
    "Ireland",
    "Greece",
  ])

  useEffect(() => {
    if (formData?.results?.trial_info?.population?.country_subject_counts) {
      setTrialCountriesFormData(formData.results.trial_info.population.country_subject_counts)
    }
  }, [])

  // auto calculate totals
  useEffect(() => {
    if (trialCountriesFormData?.length > 0) {
      let totalWorldwide = 0
      let totalEEA = 0

      trialCountriesFormData.forEach((country) => {
        totalWorldwide += Number(country.subjects)
        if (eeaSet.has(country.country_name)) {
          totalEEA += Number(country.subjects)
        }
      })

      setTotalWorldwide(totalWorldwide)
      setTotalEEA(totalEEA)
    }
  }, [trialCountriesFormData])

  const onChange = (e, i, key) => {
    let tempData = [...trialCountriesFormData]
    tempData[i][key] = e
    setTrialCountriesFormData(tempData)
    console.log("TrialCountries useEffect >> onChange")
    props.onChange(tempData)
  }

  const onAddCountry = () => {
    let newCountry = {
      country_name: "",
      subjects: "",
    }

    let tempData = [...trialCountriesFormData, newCountry]
    setTrialCountriesFormData(tempData)
  }

  const onDeleteCountry = (i) => {
    let tempData = [...trialCountriesFormData]
    tempData.splice(i, 1)
    setTrialCountriesFormData(tempData)
    console.log("TrialCountries useEffect >> onChange")
    props.onChange(tempData)
  }

  const displayRows = () => {
    return trialCountriesFormData.map((country, i) => {
      return (
        <tr
          key={`trial-country-${i}`}
          className="trial-country-table-row"
        >
          <td className="trial-country-table-desc">
            <CountryRow
              schema={props.schema}
              country={country}
              onChange={onChange}
              onDeleteCountry={onDeleteCountry}
              i={i}
              formView={"eudract"}
              readOnly={readOnly}
            />
            <CellValidation
              table="trialInfo"
              errorKey={`results_trial_info_population_country_${i + 1}_country_name`}
            />
          </td>
          <td className="trial-country-table-desc">
            <InputNumber
              readOnly={readOnly}
              value={country.subjects}
              onChange={(e) => onChange(e, i, "subjects")}
            />
            <CellValidation
              table="trialInfo"
              errorKey={`results_trial_info_population_country_${i + 1}_planned_subjects`}
            />
          </td>
        </tr>
      )
    })
  }

  useEffect(() => {
    let compareData = versionData?.study_data?.results?.trial_info?.population?.country_subject_counts

    if (compareData?.length > 0) {
      let compareTotalWorldwide = 0
      let compareTotalEEA = 0

      compareData.forEach((country) => {
        compareTotalWorldwide += Number(country.subjects)
        if (eeaSet.has(country.country_name)) {
          compareTotalEEA += Number(country.subjects)
        }
      })

      setCompareTotalWorldwide(compareTotalWorldwide)
      setCompareTotalEEA(compareTotalEEA)
    }
  }, [versionData])

  const displayCompareRows = () => {
    let compareData = versionData?.study_data?.results?.trial_info?.population?.country_subject_counts
    if (!compareData) return []

    return compareData?.map((country, i) => {
      return (
        <tr
          key={`trial-country-compare-${i}`}
          className="trial-country-table-row"
        >
          <td className="trial-country-table-desc">{country?.country_name}</td>
          <td className="trial-country-table-desc">{country?.subjects}</td>
        </tr>
      )
    })
  }

  const classNames = ["results-table", customTableWidthClassName]

  return (
    <div
      className={`${classNames.join(" ")} trial-country-container`}
      id={id + "-field"}
    >
      <table>
        <thead>
          <tr>
            {schema.headers.map((header, i) => {
              let minWidth = 200

              return (
                <th
                  className="text-bold trial-country-heading "
                  key={`age-range-${header.key}`}
                  style={{
                    minWidth: i === 0 ? 400 : minWidth,
                  }}
                >
                  {header.title}
                </th>
              )
            })}
          </tr>
        </thead>
        <tbody>
          {displayRows()}
          {schema.footers.map((footer, i) => {
            return (
              <tr
                key={`age-range-footer-${i}`}
                className="trial-country-table-row"
              >
                <td
                  style={{ fontWeight: "bold" }}
                  className="trial-country-table-desc"
                >
                  {footer.title}
                </td>
                <td
                  style={{ fontWeight: "bold" }}
                  className="trial-country-table-desc"
                >
                  {footer.key === "worldwide" && totalWorldwide}
                  {footer.key === "eea-only" && totalEEA}
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
      {readOnly !== true && (
        <PrimeButton
          type="add"
          size="small"
          style={{ marginTop: 16 }}
          onClick={() => onAddCountry()}
        >
          Add Country
        </PrimeButton>
      )}

      {versionData && selectedVersion && showVersionCompare && (
        <TableVersionComparisonWrapper>
          <table>
            <thead>
              <tr className="trial-country-table-row">
                {schema.headers.map((header, i) => {
                  let minWidth = 200
                  return (
                    <th
                      className="trial-country-heading"
                      key={`age-range-compare-${header.key}`}
                      style={{
                        minWidth: i === 0 ? 400 : minWidth,
                      }}
                    >
                      {header.title}
                    </th>
                  )
                })}
              </tr>
            </thead>
            <tbody>
              {displayCompareRows()}
              {schema.footers.map((footer, i) => {
                return (
                  <tr
                    key={`age-range-footer-compare-${i}`}
                    className="trial-country-table-row"
                  >
                    <td
                      style={{ fontWeight: "bold" }}
                      className="trial-country-table-desc"
                    >
                      {footer.title}
                    </td>
                    <td
                      style={{ fontWeight: "bold" }}
                      className="trial-country-table-desc"
                    >
                      {footer.key === "worldwide" && compareTotalWorldwide}
                      {footer.key === "eea-only" && compareTotalEEA}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </TableVersionComparisonWrapper>
      )}
    </div>
  )
}

export default RJTrialCountries
